import { Fragment, useEffect, useMemo, useState } from "react"

// ** Third Party Components
import { z } from "zod"
import { he } from "date-fns/locale"
import { Loader } from "lucide-react"
import { useForm } from "react-hook-form"
import { useLocation } from "react-router-dom"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Add, Calendar, CloseCircle, InfoCircle } from "iconsax-reactjs"
import * as SelectPrimitive from "@radix-ui/react-select"
import { CheckIcon } from "@radix-ui/react-icons"

// ** Theme Components
import { Label } from "@pma/components/common/label"
import { Switch } from "@pma/components/common/switch"
import CalendarDate from "@pma/components/common/CalendarDate"
import { Avatar, AvatarImage } from "@pma/components/common/avatar"
import { CalendarElement } from "@pma/components/common/customCalendar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@pma/components/common/tooltip"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@pma/components/common/form"
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@pma/components/common/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@pma/components/common/select"

// ** Store
import { useAuth } from "@pma/stores/auth-store"
import { useCommonStore } from "@pma/stores/common-store"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Service
import {
  createNewProjectService,
  getProjectStatusListService,
  getProjectWorkTypeListService,
} from "@pma/services/project"

// ** Types
import { DictType } from "@pma/types"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import GeoLocationAddress from "./GeoLocationAddress"
import TeamMembersDialog from "@pma/components/TeamMembers"

const projectSchema = z.object({
  title: z.string().min(1, "Project name is required"),
  start_at: z.string(),
  finish_to: z.string(),
  use_gis: z.boolean(),
  address_text: z.string().optional(),
  description: z.string(),
  owner_id: z.number(),
  status_id: z.string(),
  organization_user_id: z.number(),
  type_id: z.string(),
  group_id: z.string(),
})

type ProjectFormValues = z.infer<typeof projectSchema>

interface ProjectInsertUpdateDialogProps {
  isMobile?: boolean
  onClose: () => void
}

const ProjectInsertUpdateDialog = ({
  isMobile = false,
  onClose,
}: ProjectInsertUpdateDialogProps) => {
  const location = useLocation()
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const { projectGroupList, setProjectUpdateAction } = useCommonStore()

  const isHebrew = locale === "he"
  const path = location.pathname.split("/")
  const orgId = authConfig?.last_organization_id

  const [showMoreFields, setShowMoreFields] = useState<boolean>(false)
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<any[]>([])
  const [teamMembersDialog, setTeamMembersDialog] = useState<boolean>(false)

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      title: "",
      owner_id: authConfig.id,
      use_gis: false,
      description: "",
      address_text: "",
      start_at: "",
      finish_to: "",
      status_id: "",
      type_id: "",
      group_id: "",
      organization_user_id: orgId,
    },
  })

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form

  useEffect(() => {
    if (path.includes("project-group")) {
      const groupId = path[path.length - 1]
      setValue("group_id", groupId)
    }
  }, [path])

  const projectGroupTree = useMemo(() => {
    const map = new Map<number, any>()
    projectGroupList?.forEach((g: any) => map.set(g.id, { ...g, _children: [] }))
    const roots: any[] = []
    map.forEach((node: any) => {
      const parent = node.parent_group_id != null ? map.get(node.parent_group_id) : null
      if (parent) parent._children.push(node)
      else roots.push(node)
    })
    const ordered: { id: number; name: string; level: number }[] = []
    const walk = (nodes: any[], level: number) => {
      nodes.forEach((node) => {
        ordered.push({ id: node.id, name: node.name, level })
        if (node._children?.length) walk(node._children, level + 1)
      })
    }
    walk(roots, 0)
    return ordered
  }, [projectGroupList])

  const { data: projectStatusList } = useQuery({
    queryKey: ["get-project-status-list", orgId],
    queryFn: () =>
      getProjectStatusListService({
        context_type: "organization user",
        context_id: orgId,
        for_project: true,
      }),
    enabled: !!orgId,
    retry: 0,
  })

  const { data: projectWorkTypeList } = useQuery({
    queryKey: ["get-project-work-type-list", orgId],
    queryFn: () => getProjectWorkTypeListService({ organization_id: orgId }),
    enabled: !!orgId,
    retry: 0,
  })

  const onSubmit = (data: ProjectFormValues) => createProjectMutation.mutate(data)

  const createProjectMutation = useMutation({
    mutationKey: ["create-new-project"],
    mutationFn: createNewProjectService,
    onSuccess: (data) => {
      onClose()
      setProjectUpdateAction(true)
      toast({
        title: translate("Success"),
        description: `${data?.title} ${translate("project created successfully")}`,
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data?.message || translate("Unable to create project"),
        variant: "error",
      })
    },
  })

  return (
    <Fragment>
      {/* Header */}
      {!isMobile ? (
        <div className='flex items-center justify-between border-b border-neutral-200 px-5 py-4'>
          <h2 className='font-[inter] text-base font-medium text-neutral-800'>
            {translate("New Project")}
          </h2>
          <CloseCircle className='cursor-pointer text-neutral-800' size={24} onClick={onClose} />
        </div>
      ) : null}

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            className={cn("flex flex-col gap-3 overflow-scroll border-b", isMobile ? "p-3" : "p-6")}
            style={isMobile ? {} : { maxHeight: "calc(100vh - 320px)" }}
          >
            <FormField
              control={control}
              name='group_id'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{translate("Project Group")}:</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className={cn(isHebrew ? "rtl" : "ltr")}>
                        <SelectValue placeholder={translate("Select Project Group")} />
                      </SelectTrigger>
                      <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                        {projectGroupTree?.map((group) => (
                          <SelectPrimitive.Item
                            key={group.id}
                            value={String(group.id)}
                            className='relative flex w-full cursor-default select-none items-center rounded-lg py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                          >
                            <span
                              className={cn(
                                "absolute flex h-3.5 w-3.5 items-center justify-center",
                                isHebrew ? "left-2" : "right-2",
                              )}
                            >
                              <SelectPrimitive.ItemIndicator>
                                <CheckIcon className='h-4 w-4' />
                              </SelectPrimitive.ItemIndicator>
                            </span>
                            <span
                              className='flex items-center gap-1'
                              style={{ paddingInlineStart: `${group.level * 16}px` }}
                            >
                              {group.level > 0 && (
                                <span
                                  aria-hidden
                                  className='mb-1 inline-block h-2 w-2 border-b border-s border-neutral-400'
                                />
                              )}
                              <SelectPrimitive.ItemText>
                                {translate(group.name)}
                              </SelectPrimitive.ItemText>
                            </span>
                          </SelectPrimitive.Item>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name='title'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {translate("Project Name")}: <small className='text-red-500'>*</small>
                  </FormLabel>
                  <FormControl>
                    <input
                      {...field}
                      className={cn(
                        "w-full rounded-lg border px-3 py-2 text-sm outline-none",
                        errors?.title
                          ? "border-error-300 focus:border-error-500 focus:ring-2 focus:ring-error-500"
                          : "border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                      )}
                      placeholder={translate("Enter project name")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showMoreFields ? (
              <>
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                  <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                    <FormField
                      control={control}
                      name='status_id'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{translate("Project Status")}</FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger className={cn(isHebrew ? "rtl" : "ltr")}>
                                <SelectValue placeholder={translate("Select Project Status")} />
                              </SelectTrigger>
                              <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                                {projectStatusList?.map((status: any, index: number) => (
                                  <SelectItem
                                    value={String(status.id)}
                                    className='rounded-lg'
                                    key={index}
                                  >
                                    {translate(status?.name)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name='type_id'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{translate("Project Type")}</FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger className={cn(isHebrew ? "rtl" : "ltr")}>
                                <SelectValue placeholder={translate("Select Project Type")} />
                              </SelectTrigger>
                              <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                                {projectWorkTypeList?.map((type: any, index: number) => (
                                  <SelectItem
                                    value={String(type?.id)}
                                    className='rounded-lg'
                                    key={index}
                                  >
                                    {translate(isHebrew ? type?.he : type?.en)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormItem>
                    <FormLabel>{translate("Date Range")}</FormLabel>
                    <FormControl>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type='button'
                            className='flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-2 hover:border-indigo-500 focus:outline-none'
                          >
                            <CalendarDate
                              startDate={watch("start_at")}
                              endDate={watch("finish_to")}
                            />
                            <Calendar className='h-4 w-4' />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className='w-auto p-0' align='start'>
                          <CalendarElement
                            initialFocus
                            mode='range'
                            locale={isHebrew ? he : undefined}
                            numberOfMonths={2}
                            selected={{
                              from:
                                watch("start_at") == "" ? undefined : new Date(watch("start_at")),
                              to:
                                watch("finish_to") == "" ? undefined : new Date(watch("finish_to")),
                            }}
                            onSelect={(range: any) => {
                              if (range?.from) {
                                setValue(
                                  "start_at",
                                  range.from.toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  }),
                                )
                              }
                              if (range?.to) {
                                setValue(
                                  "finish_to",
                                  range.to.toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  }),
                                )
                              }
                            }}
                          />
                          <div className='flex w-full justify-center p-2'>
                            <PopoverClose>
                              <button
                                type='button'
                                className='w-[200px] rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600'
                              >
                                {translate("Save")}
                              </button>
                            </PopoverClose>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                  </FormItem>
                </div>
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                  <FormField
                    control={control}
                    name='use_gis'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{translate("Support GIS")}</FormLabel>
                        <FormControl>
                          <div className='flex gap-6'>
                            <Switch
                              checked={field.value}
                              className='h-6 w-11'
                              onCheckedChange={field.onChange}
                            />
                            <Tooltip>
                              <TooltipTrigger>
                                <InfoCircle size={16} className='text-slate-500' />
                              </TooltipTrigger>
                              <TooltipContent className='bg-indigo-600'>
                                {translate("If set to True, the project will support GIS data")}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name='address_text'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{translate("Address")}</FormLabel>
                        <FormControl>
                          <GeoLocationAddress
                            disabled={!watch("use_gis")}
                            location={field?.value || ""}
                            setGeoLocation={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={control}
                  name='description'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{translate("Project Description")}</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          rows={3}
                          className='w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500'
                          placeholder={translate("Enter project description")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className='flex items-center gap-4'>
                  <label className='w-1/5 text-sm font-medium text-gray-700'>
                    {translate("Project Manager")}
                  </label>
                  <div>
                    <div className='flex flex-wrap gap-3'>
                      <Label
                        className={cn(
                          "flex items-center gap-1 rounded-full border border-neutral-200 py-1",
                          isHebrew ? "pl-4 pr-1" : "pl-1 pr-4",
                        )}
                      >
                        <Avatar className='h-7 w-7'>
                          <AvatarImage
                            src={
                              authConfig.user_picture.url ||
                              `${process.env.PUBLIC_URL}/user-profile.svg`
                            }
                          />
                        </Avatar>{" "}
                        {authConfig?.name}
                      </Label>
                    </div>
                  </div>
                </div>
                <div className='flex items-center gap-4'>
                  <label className='w-1/5 text-sm font-medium text-gray-700'>
                    {translate("Team Members")}
                  </label>
                  <div className='flex w-4/5 items-center gap-3'>
                    {selectedTeamMembers.slice(0, 2).map((user) => (
                      <Label
                        key={user.id}
                        htmlFor={`user_${user?.id}`}
                        className={cn(
                          "flex shrink-0 items-center gap-1 rounded-full border border-neutral-200 py-1",
                          isHebrew ? "pl-4 pr-1" : "pl-1 pr-4",
                        )}
                      >
                        <Avatar className='h-7 w-7'>
                          <AvatarImage
                            src={
                              user?.user_picture?.url ||
                              `${process.env.PUBLIC_URL}/user-profile.svg`
                            }
                          />
                        </Avatar>{" "}
                        <span className='max-w-[80px] truncate text-sm'>{user?.name}</span>
                      </Label>
                    ))}
                    {selectedTeamMembers.length > 2 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type='button'
                            className='flex shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100'
                          >
                            +{selectedTeamMembers.length - 2}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className='w-56 p-2' align='start'>
                          <div className='flex flex-col gap-1'>
                            {selectedTeamMembers.slice(2).map((user) => (
                              <div
                                key={user.id}
                                className='flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-neutral-50'
                              >
                                <Avatar className='h-7 w-7'>
                                  <AvatarImage
                                    src={
                                      user?.user_picture?.url ||
                                      `${process.env.PUBLIC_URL}/user-profile.svg`
                                    }
                                  />
                                </Avatar>
                                <span className='truncate text-sm text-neutral-700'>
                                  {user?.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    <button
                      type='button'
                      className='flex shrink-0 items-center gap-2 rounded-xl border border-neutral-200 px-4 py-2 text-sm font-medium shadow hover:text-indigo-700'
                      onClick={() => setTeamMembersDialog(true)}
                    >
                      <Add className='h-4 w-4' />
                      {translate("Invite")}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p
                onClick={() => setShowMoreFields(true)}
                className='cursor-pointer text-sm font-medium text-indigo-600 hover:text-indigo-700'
              >
                {translate("Add more info to the project")}
              </p>
            )}
          </div>
          <div className={cn("flex items-center justify-end gap-3", isMobile ? "p-3" : "p-6")}>
            <button
              className='rounded-full border border-neutral-200 px-6 py-2 font-[inter] text-sm font-normal text-neutral-800'
              type='button'
              onClick={onClose}
            >
              {translate("Cancel")}
            </button>
            <button
              className='flex rounded-full bg-indigo-600 px-6 py-2 font-[inter] text-sm font-medium text-white shadow'
              type='submit'
              disabled={createProjectMutation.isPending}
              style={{
                background:
                  "linear-gradient(0deg, var(--Indigo-8, #444CE7), var(--Indigo-8, #444CE7)), linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
              }}
            >
              {createProjectMutation.isPending ? (
                <Loader className={cn("animate-spin", isHebrew ? "ml-1" : "mr-1")} size={16} />
              ) : null}{" "}
              {translate("Save")}
            </button>
          </div>
        </form>
      </Form>

      {teamMembersDialog && (
        <TeamMembersDialog
          teamMembersDialog={teamMembersDialog}
          selectedTeamMembers={selectedTeamMembers}
          setTeamMembersDialog={setTeamMembersDialog}
          setSelectedTeamMembers={setSelectedTeamMembers}
        />
      )}
    </Fragment>
  )
}

export default ProjectInsertUpdateDialog
